import { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Search, Upload, Loader2, Calendar, Globe, User, ChevronDown, ChevronUp, Info, BookUser, CreditCard, MapPin, Home } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`
});

export default function ScanPage({ user, onLogout }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  // Single scan fields
  const [singleName, setSingleName] = useState('');
  const [dob, setDob] = useState('');
  const [nationality, setNationality] = useState('');
  const [gender, setGender] = useState('');
  const [passportNumber, setPassportNumber] = useState('');
  const [nationalId, setNationalId] = useState('');
  const [placeOfBirth, setPlaceOfBirth] = useState('');
  const [address, setAddress] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Bulk scan
  const [bulkText, setBulkText] = useState('');
  const [bulkMode, setBulkMode] = useState('simple'); // simple | structured

  useEffect(() => {
    if (user.role === 'viewer') {
      toast.error('You do not have permission to perform scans');
      navigate('/');
    }
  }, [user, navigate]);

  const handleSingleScan = async (e) => {
    e.preventDefault();
    if (!singleName.trim()) { toast.error('Please enter a name'); return; }

    setLoading(true);
    try {
      const payload = { name: singleName, entity_type: 'individual' };
      if (dob) payload.date_of_birth = dob;
      if (nationality) payload.nationality = nationality;
      if (gender) payload.gender = gender;
      if (passportNumber) payload.passport_number = passportNumber;
      if (nationalId) payload.national_id = nationalId;
      if (placeOfBirth) payload.place_of_birth = placeOfBirth;
      if (address) payload.address = address;

      const response = await axios.post(`${API}/scan`, payload, { headers: getAuthHeaders() });
      toast.success('Scan completed successfully');
      navigate('/results', { state: { scanResult: response.data } });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Scan failed');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkScan = async (e) => {
    e.preventDefault();
    const lines = bulkText.split('\n').map(l => l.trim()).filter(l => l.length > 0);
    if (lines.length === 0) { toast.error('Please enter at least one entry'); return; }

    setLoading(true);
    try {
      let payload;

      if (bulkMode === 'structured') {
        // Parse CSV-style: Name, DOB, Nationality, Gender
        const entries = lines.map(line => {
          const parts = line.split(',').map(p => p.trim());
          return {
            name: parts[0] || '',
            date_of_birth: parts[1] || null,
            nationality: parts[2] || null,
            gender: parts[3] || null,
            passport_number: parts[4] || null,
            national_id: parts[5] || null,
          };
        }).filter(e => e.name);
        payload = { entries };
      } else {
        payload = { names: lines };
      }

      const response = await axios.post(`${API}/scan/bulk`, payload, { headers: getAuthHeaders() });
      toast.success(`Scanned ${response.data.total} entries successfully`);
      navigate('/results');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bulk scan failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout user={user} onLogout={onLogout} activePage="scan">
      <div className="max-w-4xl mx-auto space-y-8">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900" data-testid="scan-page-title">
            Name Screening
          </h1>
          <p className="text-sm text-slate-600 mt-2">
            Screen individuals and entities against OFAC, PEP, MOHA, and UNSCR watchlists
          </p>
        </div>

        <Tabs defaultValue="single" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-8">
            <TabsTrigger value="single" data-testid="tab-single-scan">
              <Search className="w-4 h-4 mr-2" />Single Scan
            </TabsTrigger>
            <TabsTrigger value="bulk" data-testid="tab-bulk-scan">
              <Upload className="w-4 h-4 mr-2" />Bulk Scan
            </TabsTrigger>
          </TabsList>

          {/* ───── Single Scan ───── */}
          <TabsContent value="single">
            <Card className="border border-slate-200 shadow-sm">
              <div className="p-8">
                <form onSubmit={handleSingleScan} className="space-y-6">
                  <div>
                    <Label htmlFor="name" className="text-slate-700 font-medium">Full Name *</Label>
                    <Input
                      id="name" type="text" placeholder="e.g., Ahmad bin Abdullah"
                      value={singleName} onChange={(e) => setSingleName(e.target.value)}
                      className="mt-2 h-12 text-base" data-testid="input-single-name"
                    />
                  </div>

                  {/* Toggle advanced fields */}
                  <button
                    type="button"
                    onClick={() => setShowAdvanced(!showAdvanced)}
                    className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800 transition-colors"
                    data-testid="toggle-advanced-fields"
                  >
                    {showAdvanced ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    {showAdvanced ? 'Hide' : 'Show'} identity verification fields
                  </button>

                  {showAdvanced && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-slate-50 border border-slate-200 rounded-lg" data-testid="advanced-fields-panel">
                      <div>
                        <Label htmlFor="dob" className="text-slate-600 text-sm flex items-center gap-1.5">
                          <Calendar className="w-3.5 h-3.5" /> Date of Birth
                        </Label>
                        <Input
                          id="dob" type="date" value={dob}
                          onChange={(e) => setDob(e.target.value)}
                          className="mt-1.5" data-testid="input-dob"
                        />
                      </div>
                      <div>
                        <Label htmlFor="nationality" className="text-slate-600 text-sm flex items-center gap-1.5">
                          <Globe className="w-3.5 h-3.5" /> Nationality
                        </Label>
                        <Input
                          id="nationality" type="text" placeholder="e.g., MY, US, IR"
                          value={nationality} onChange={(e) => setNationality(e.target.value)}
                          className="mt-1.5" data-testid="input-nationality"
                        />
                      </div>
                      <div>
                        <Label htmlFor="gender" className="text-slate-600 text-sm flex items-center gap-1.5">
                          <User className="w-3.5 h-3.5" /> Gender
                        </Label>
                        <select
                          id="gender" value={gender} onChange={(e) => setGender(e.target.value)}
                          className="mt-1.5 w-full h-9 rounded-md border border-slate-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                          data-testid="input-gender"
                        >
                          <option value="">-- Select --</option>
                          <option value="male">Male</option>
                          <option value="female">Female</option>
                        </select>
                      </div>

                      <div className="md:col-span-3 grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="passport" className="text-slate-600 text-sm flex items-center gap-1.5">
                            <BookUser className="w-3.5 h-3.5" /> Passport Number
                          </Label>
                          <Input
                            id="passport" type="text" placeholder="e.g., A1234567"
                            value={passportNumber} onChange={(e) => setPassportNumber(e.target.value)}
                            className="mt-1.5" data-testid="input-passport"
                          />
                        </div>
                        <div>
                          <Label htmlFor="national-id" className="text-slate-600 text-sm flex items-center gap-1.5">
                            <CreditCard className="w-3.5 h-3.5" /> National ID / NRIC
                          </Label>
                          <Input
                            id="national-id" type="text" placeholder="e.g., 850315-14-5678"
                            value={nationalId} onChange={(e) => setNationalId(e.target.value)}
                            className="mt-1.5" data-testid="input-national-id"
                          />
                        </div>
                        <div>
                          <Label htmlFor="pob" className="text-slate-600 text-sm flex items-center gap-1.5">
                            <MapPin className="w-3.5 h-3.5" /> Place of Birth
                          </Label>
                          <Input
                            id="pob" type="text" placeholder="e.g., Kuala Lumpur, Malaysia"
                            value={placeOfBirth} onChange={(e) => setPlaceOfBirth(e.target.value)}
                            className="mt-1.5" data-testid="input-place-of-birth"
                          />
                        </div>
                        <div>
                          <Label htmlFor="address" className="text-slate-600 text-sm flex items-center gap-1.5">
                            <Home className="w-3.5 h-3.5" /> Address
                          </Label>
                          <Input
                            id="address" type="text" placeholder="e.g., Jalan Ampang, Kuala Lumpur"
                            value={address} onChange={(e) => setAddress(e.target.value)}
                            className="mt-1.5" data-testid="input-address"
                          />
                        </div>
                      </div>

                      <div className="md:col-span-3 flex items-start gap-2 text-xs text-slate-500 bg-blue-50 border border-blue-100 rounded p-2.5">
                        <Info className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />
                        <span>
                          Every identifier you supply is scored: a matching <strong>passport or national ID</strong> confirms
                          an identity outright, date of birth, nationality, place of birth and address raise or lower
                          confidence, and identifiers that <strong>contradict</strong> the listing reduce it.
                          Native-script names (Arabic, Cyrillic, Chinese) are screened as entered and as transliterated.
                        </span>
                      </div>
                    </div>
                  )}

                  <Button
                    type="submit" disabled={loading}
                    className="w-full bg-slate-900 hover:bg-slate-800 text-white h-12"
                    data-testid="button-submit-single-scan"
                  >
                    {loading
                      ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Scanning...</>
                      : <><Search className="w-4 h-4 mr-2" />Run Scan</>}
                  </Button>
                </form>
              </div>
            </Card>
          </TabsContent>

          {/* ───── Bulk Scan ───── */}
          <TabsContent value="bulk">
            <Card className="border border-slate-200 shadow-sm">
              <div className="p-8">
                <form onSubmit={handleBulkScan} className="space-y-6">
                  {/* Mode toggle */}
                  <div className="flex items-center gap-3" data-testid="bulk-mode-toggle">
                    <button type="button"
                      onClick={() => setBulkMode('simple')}
                      className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${bulkMode === 'simple' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                      data-testid="bulk-mode-simple"
                    >
                      Names Only
                    </button>
                    <button type="button"
                      onClick={() => setBulkMode('structured')}
                      className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${bulkMode === 'structured' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                      data-testid="bulk-mode-structured"
                    >
                      Structured (CSV)
                    </button>
                  </div>

                  <div>
                    <Label htmlFor="bulk-names" className="text-slate-700 font-medium">
                      {bulkMode === 'simple' ? 'Names List' : 'Structured Entries'}
                    </Label>
                    <Textarea
                      id="bulk-names"
                      placeholder={bulkMode === 'simple'
                        ? 'John Doe\nJane Smith\nAcme Corporation\n...'
                        : 'Ahmad bin Abdullah, 1985-03-15, MY, male\nJohn Smith, 1970-06-01, US, male\nAcme Corp\n...'}
                      value={bulkText}
                      onChange={(e) => setBulkText(e.target.value)}
                      className="mt-2 min-h-[280px] font-mono text-sm"
                      data-testid="textarea-bulk-names"
                    />
                    <p className="text-xs text-slate-500 mt-2">
                      {bulkMode === 'simple'
                        ? 'One name per line (max 100)'
                        : 'CSV format: Name, DOB (YYYY-MM-DD), Nationality, Gender, Passport, National ID — one per line (max 100). Only Name is required.'}
                    </p>
                  </div>

                  <Button
                    type="submit" disabled={loading}
                    className="w-full bg-slate-900 hover:bg-slate-800 text-white h-12"
                    data-testid="button-submit-bulk-scan"
                  >
                    {loading
                      ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Scanning...</>
                      : <><Upload className="w-4 h-4 mr-2" />Run Bulk Scan</>}
                  </Button>
                </form>
              </div>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="p-4 border border-blue-200 bg-blue-50">
            <h3 className="font-semibold text-blue-900 text-sm mb-1">Fuzzy Matching</h3>
            <p className="text-xs text-blue-700">Advanced algorithms detect name variations and misspellings</p>
          </Card>
          <Card className="p-4 border border-purple-200 bg-purple-50">
            <h3 className="font-semibold text-purple-900 text-sm mb-1">Identity Verification</h3>
            <p className="text-xs text-purple-700">Multi-factor matching using DOB, nationality, and gender for confirmed results</p>
          </Card>
          <Card className="p-4 border border-emerald-200 bg-emerald-50">
            <h3 className="font-semibold text-emerald-900 text-sm mb-1">5 Data Sources</h3>
            <p className="text-xs text-emerald-700">Screens against OFAC SDN, PEP, MY MOHA, UNSCR, and OpenSanctions</p>
          </Card>
        </div>
      </div>
    </Layout>
  );
}
